<template>
  <div class="sc-give-form-trial">
    <!-- Alert Banner -->
    <div class="sc-give-form-trial__alert">
      <span class="sc-give-form-trial__alert-text">Upgrade account to activate giving</span>
      
      <!-- Alert Icon with hover tooltip (CSS-only, no Bootstrap Vue popover) -->
      <div class="sc-give-form-trial__tooltip-wrap">
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 16 16" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg" 
          class="sc-give-form-trial__alert-icon"
        >
          <path d="M8 16C3.5625 16 0 12.4375 0 8C0 3.59375 3.5625 0 8 0C12.4062 0 16 3.59375 16 8C16 12.4375 12.4062 16 8 16ZM5.28125 5.1875V5.21875C5.15625 5.59375 5.34375 6.03125 5.75 6.1875C6.125 6.3125 6.5625 6.125 6.6875 5.71875L6.71875 5.6875C6.75 5.59375 6.84375 5.53125 6.9375 5.53125H8.75C9.03125 5.53125 9.25 5.71875 9.25 6C9.25 6.15625 9.15625 6.3125 9 6.40625L7.625 7.1875C7.375 7.3125 7.25 7.5625 7.25 7.84375V8.25C7.25 8.6875 7.5625 9 8 9C8.40625 9 8.71875 8.6875 8.75 8.28125L9.75 7.6875C10.3438 7.34375 10.75 6.6875 10.75 6C10.75 4.90625 9.84375 4 8.75 4H6.9375C6.1875 4 5.53125 4.46875 5.28125 5.1875ZM9 11C9 10.4688 8.53125 10 8 10C7.4375 10 7 10.4688 7 11C7 11.5625 7.4375 12 8 12C8.53125 12 9 11.5625 9 11Z" fill="currentColor"/>
        </svg>

        <!-- Tooltip panel — stays visible because it's inside the same :hover parent -->
        <div class="sc-give-form-trial__tooltip">
          <!-- White arrow (pure CSS triangle, no Bootstrap Vue .arrow override needed) -->
          <div class="sc-give-form-trial__tooltip-arrow"></div>
          <div class="sc-give-form-trial__tooltip-inner">
            <p class="sc-give-form-trial__tooltip-title">Trial accounts don't include giving</p>
            <p class="sc-give-form-trial__tooltip-body">Upgrade your account to get full access to Amplify Streaming capabilities</p>
            <div class="sc-give-form-trial__tooltip-actions">
              <a @click.prevent="handleLearnMoreClick" href="javascript:void(0)" class="sc-give-form-trial__tooltip-link">
                Learn more
              </a>
              <a @click.prevent="handleUpgradeClick" href="javascript:void(0)" class="sc-give-form-trial__tooltip-upgrade">
                Upgrade
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="sc-give-form-trial__tabs">
      <button 
        @click="activeTab = 'give'"
        :class="['sc-give-form-trial__tab', { 'sc-give-form-trial__tab--active': activeTab === 'give' }]"
      >
        <span>Give Now</span>
      </button>
      <button 
        @click="activeTab = 'signin'"
        :class="['sc-give-form-trial__tab', { 'sc-give-form-trial__tab--active': activeTab === 'signin' }]"
      >
        <span>Sign In</span>
      </button>
    </div>

    <!-- Content -->
    <div v-if="activeTab === 'give'" class="sc-give-form-trial__content">
      <!-- Title -->
      <div class="sc-give-form-trial__title">Help Support Our Ministry</div>

      <!-- Amount Display -->
      <div class="sc-give-form-trial__amount-display">$0.00</div>

      <!-- Amount Divider -->
      <div class="sc-give-form-trial__amount-divider"></div>

      <!-- Gift Type Selector -->
      <div class="sc-give-form-trial__gift-selector">
        <button class="sc-give-form-trial__gift-option" disabled>
          <span>Recurring Gift</span>
        </button>
        <button class="sc-give-form-trial__gift-option sc-give-form-trial__gift-option--active" disabled>
          <span>One-Time Gift</span>
        </button>
      </div>

      <!-- Fund Selector -->
      <div class="sc-give-form-trial__fund-selector" disabled>
        <span class="sc-give-form-trial__fund-text">General Fund</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="sc-give-form-trial__fund-icon">
          <path d="M7 10l5 5 5-5z" fill="currentColor"/>
        </svg>
      </div>

      <!-- Checkbox -->
      <div class="sc-give-form-trial__checkbox-group">
        <input type="checkbox" class="sc-give-form-trial__checkbox" disabled />
        <label class="sc-give-form-trial__checkbox-label">Schedule this one-time gift on a future date</label>
      </div>

      <!-- Add Another Gift Button -->
      <button class="sc-give-form-trial__secondary-button" disabled>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="sc-give-form-trial__secondary-button-icon">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
        </svg>
        <span>Add Another Gift</span>
      </button>

      <!-- Give Button -->
      <button class="sc-give-form-trial__give-button" disabled>
        Give
      </button>
    </div>

    <!-- Sign In Content -->
    <div v-if="activeTab === 'signin'" class="sc-give-form-trial__content sc-give-form-trial__content--signin">
      <!-- Ministry Brand Header -->
      <div class="sc-give-form-trial__signin-header">
        <svg 
          width="150" 
          height="34" 
          viewBox="0 0 150 34" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          class="sc-give-form-trial__signin-logo"
        >
          <g clip-path="url(#clip0_15275_2258)">
            <path d="M12.3873 0.671463L7.65029 11.0463L2.91325 0.671463H1.65547L0 14.2944H2.24366L3.187 5.57497L7.06394 14.2944H8.25883L12.1987 5.59717L13.142 14.2944H15.3006L13.6248 0.671463H12.3873Z" fill="#363636"/>
            <path d="M20.175 3.81407H17.8906V14.2944H20.175V3.81407Z" fill="#363636"/>
            <path d="M38.133 3.81407H35.8486V14.2944H38.133V3.81407Z" fill="#363636"/>
            <path d="M48.556 11.4237C48.556 9.18 46.2513 8.51042 44.6365 7.90187C43.4842 7.4413 42.8331 7.12685 42.8331 6.41288C42.8331 5.76364 43.3787 5.28087 44.4053 5.28087C45.4319 5.28087 46.2716 5.72109 46.858 6.39253H47.0263L48.2009 5.3863V5.21798C47.4462 4.21175 46.0201 3.56252 44.3646 3.56252C42.0581 3.56252 40.5506 4.79995 40.5506 6.559C40.5506 8.44568 42.2689 9.09492 43.8837 9.68127C45.1618 10.164 46.231 10.5192 46.231 11.4847C46.231 12.2819 45.4559 12.8257 44.3239 12.8257C43.1087 12.8257 42.0396 12.1968 41.4532 11.3996H41.2849L40.1104 12.4262V12.5945C41.0537 13.7691 42.6259 14.5441 44.4904 14.5441C46.7544 14.5441 48.556 13.4121 48.556 11.4218V11.4237Z" fill="#363636"/>
            <path d="M51.4266 10.7948C51.4266 13.1642 52.9563 14.5459 54.9688 14.5459C55.6606 14.5459 56.3524 14.3776 56.9184 13.9799L56.9812 13.8116L56.3098 12.3651L56.1637 12.2819C55.8696 12.4706 55.618 12.576 55.2832 12.576C54.277 12.576 53.711 11.8639 53.711 10.6264V5.59531H56.898V3.81406H53.711V0.523477L53.5427 0.418045L51.4248 0.732491V3.81406H49.6436V5.59531H51.4248V10.7929L51.4266 10.7948Z" fill="#363636"/>
            <path d="M61.7918 8.29955C61.7918 6.5812 62.7148 5.53243 64.0558 5.53243C64.5164 5.53243 64.8937 5.59532 65.2933 5.82653L65.4394 5.76364L66.0257 4.08783L65.9425 3.9195C65.4394 3.68829 64.8937 3.56252 64.287 3.56252C63.0921 3.56252 62.1063 4.06563 61.4163 4.90353C61.1648 4.04343 60.4933 3.56252 59.5925 3.56252C59.0062 3.56252 58.5863 3.70864 58.2922 3.9195L58.209 4.06563L58.5438 5.61751L58.6899 5.6804C58.7731 5.61751 58.8786 5.55462 59.0247 5.55462C59.4224 5.55462 59.5075 5.91161 59.5075 6.47761V14.2962H61.7918V8.3014V8.29955Z" fill="#363636"/>
            <path d="M93.4382 10.5229C93.4382 8.8045 92.16 7.69284 90.9226 7.16938C91.9714 6.64592 92.8093 5.70258 92.8093 4.29867C92.8093 2.30656 91.6569 0.673294 88.2406 0.673294H82.7285V14.2981H88.4514C92.0139 14.2981 93.44 12.6426 93.44 10.5247L93.4382 10.5229ZM85.0536 2.55812H88.0722C89.5612 2.55812 90.4417 3.2499 90.4417 4.50769C90.4417 5.76547 89.5612 6.45725 88.0722 6.45725H85.0536V2.55627V2.55812ZM85.0536 8.17561H88.5328C90.0847 8.17561 91.1113 9.01351 91.1113 10.2935C91.1113 11.5735 90.0847 12.4114 88.5328 12.4114H85.0536V8.17746V8.17561Z" fill="#363636"/>
            <path d="M98.1121 8.29955C98.1121 6.5812 99.0351 5.53243 100.376 5.53243C100.837 5.53243 101.214 5.59532 101.614 5.82653L101.76 5.76364L102.346 4.08783L102.263 3.9195C101.76 3.68829 101.214 3.56252 100.607 3.56252C99.4125 3.56252 98.4266 4.06563 97.7367 4.90353C97.4851 4.04343 96.8137 3.56252 95.9129 3.56252C95.3265 3.56252 94.9066 3.70864 94.6125 3.9195L94.5293 4.06563L94.8641 5.61751L95.0102 5.6804C95.0934 5.61751 95.1989 5.55462 95.345 5.55462C95.7427 5.55462 95.8278 5.91161 95.8278 6.47761V14.2962H98.1121V8.3014V8.29955Z" fill="#363636"/>
            <path d="M111.454 3.81407V5.07186C110.531 4.17106 109.274 3.56252 107.744 3.56252C104.662 3.56252 102.483 5.97265 102.483 9.05422C102.483 12.1136 104.664 14.5459 107.744 14.5459C109.274 14.5459 110.554 13.9374 111.475 13.0162C111.706 13.9799 112.398 14.5459 113.424 14.5459C113.439 14.5459 113.452 14.5459 113.467 14.5459V3.81407H111.454ZM108.101 12.5557C106.111 12.5557 104.81 11.0038 104.81 9.05607C104.81 7.10836 106.088 5.55462 108.121 5.55462C109.925 5.55462 111.391 6.79206 111.391 9.05607C111.391 11.3201 109.925 12.5557 108.101 12.5557Z" fill="#363636"/>
            <path d="M139.431 1.52588e-05L137.313 0.314461V4.84248C136.433 4.06747 135.258 3.56435 133.917 3.56435C130.732 3.56435 128.594 5.97449 128.594 9.05606C128.594 12.1376 130.732 14.5478 133.917 14.5478C135.447 14.5478 136.705 13.9614 137.585 13.0384C137.837 13.9818 138.528 14.5478 139.492 14.5478C139.522 14.5478 139.548 14.5459 139.575 14.5459V0.105447L139.429 1.52588e-05H139.431ZM134.191 12.5557C132.241 12.5557 130.921 11.0038 130.921 9.05606C130.921 7.10834 132.241 5.55461 134.191 5.55461C136.035 5.55461 137.504 6.74951 137.504 9.05606C137.504 11.3626 136.037 12.5557 134.193 12.5557H134.191Z" fill="#363636"/>
            <path d="M146.081 7.90187C144.928 7.4413 144.277 7.12685 144.277 6.41288C144.277 5.76364 144.823 5.28087 145.85 5.28087C146.876 5.28087 147.716 5.72109 148.302 6.39253H148.471L149.645 5.3863V5.21798C148.89 4.21175 147.464 3.56252 145.809 3.56252C143.502 3.56252 141.993 4.79995 141.993 6.56085C141.993 8.44753 143.711 9.09676 145.326 9.68311C146.604 10.1659 147.673 10.521 147.673 11.4866C147.673 12.2838 146.898 12.8276 145.766 12.8276C144.551 12.8276 143.482 12.1987 142.896 11.4015H142.727L141.553 12.428V12.5964C142.496 13.7709 144.068 14.5459 145.933 14.5459C148.197 14.5459 149.998 13.4139 149.998 11.4237C149.998 9.18 147.694 8.51042 146.079 7.90187H146.081Z" fill="#363636"/>
            <path d="M33.1245 14.2944V7.98511C33.1245 5.36596 31.4265 3.56252 28.7648 3.56252C27.3609 3.56252 26.166 4.06563 25.3485 4.88319C25.0766 4.04528 24.3848 3.56252 23.4414 3.56252C22.8958 3.56252 22.4556 3.70864 22.1633 3.9195L22.0801 4.06563L22.4149 5.61751L22.561 5.6804C22.6442 5.61751 22.7497 5.55462 22.8958 5.55462C23.2935 5.55462 23.3786 5.91161 23.3786 6.47761V14.2962H25.6833V8.17562C25.6833 6.58305 26.7524 5.53428 28.3246 5.53428C29.8968 5.53428 30.8402 6.58305 30.8402 8.17562V14.2962H33.1245V14.2944Z" fill="#363636"/>
            <path d="M76.7321 12.4724V3.81406H74.4681V8.823C74.4681 10.3952 73.3361 11.4643 71.849 11.4643C70.3618 11.4643 69.4795 10.5839 69.4795 9.0949V3.81221H67.2377V9.26137C67.2377 12.0285 69.0818 13.4121 71.4087 13.4121C72.7072 13.4121 73.756 12.9515 74.5107 12.1968V12.7425C74.5107 14.6717 73.1474 15.6761 71.4494 15.6761C69.9401 15.6761 68.7249 14.9011 67.9905 13.9355H67.8222L66.7734 15.1933V15.3394C67.8 16.6176 69.3944 17.4777 71.4272 17.4777C74.2998 17.4777 76.7303 15.8222 76.7303 12.4687L76.7321 12.4724Z" fill="#363636"/>
            <path d="M122.055 3.56252C120.651 3.56252 119.456 4.06563 118.639 4.88319C118.367 4.04528 117.675 3.56252 116.731 3.56252C116.186 3.56252 115.746 3.70864 115.453 3.9195L115.37 4.06563L115.705 5.61751L115.851 5.6804C115.934 5.61751 116.04 5.55462 116.186 5.55462C116.584 5.55462 116.669 5.91161 116.669 6.47761V14.2962H118.973V8.17562C118.973 6.58305 120.042 5.53428 121.615 5.53428C123.187 5.53428 124.13 6.58305 124.13 8.17562V14.2962H126.415V7.98696C126.415 5.36781 124.717 3.56436 122.055 3.56436V3.56252Z" fill="#363636"/>
            <path d="M40.3702 23.0266L35.5 33.4107L35.5481 33.5069H36.6598L37.7566 31.1375H43.4481L44.5283 33.5069H45.6733L45.7213 33.4107L40.8844 23.0266H40.3684H40.3702ZM38.2264 30.1054L40.6125 24.9299L42.9672 30.1054H38.2264Z" fill="#363636"/>
            <path d="M57.9778 23.0266L54.0121 31.6684L50.1093 23.0266H49.5932L48.4482 33.5069H49.5285L50.3183 25.9621L53.7531 33.5069H54.2692L57.7688 25.9454L58.5586 33.5069H59.6388L58.4939 23.0266H57.9778Z" fill="#363636"/>
            <path d="M67.498 23.0266H63.7246V33.5069H64.8862V29.3637H67.498C69.8674 29.3637 71.1899 28.2187 71.1899 26.2025C71.1899 24.1716 69.8674 23.0266 67.498 23.0266ZM67.805 28.363H64.8862V24.0588H67.805C69.2071 24.0588 69.9987 24.8319 69.9987 26.2192C69.9987 27.6065 69.2089 28.363 67.805 28.363Z" fill="#363636"/>
            <path d="M75.7047 32.4748V23.0266H74.5449V33.5069H80.9134V32.4748H75.7047Z" fill="#363636"/>
            <path d="M85.3237 23.0266H84.1621V33.5069H85.3237V23.0266Z" fill="#363636"/>
            <path d="M89.9346 33.5069H91.0962V28.7348H95.3042V27.7026H91.0962V24.0588H96.303V23.0266H89.9346V33.5069Z" fill="#363636"/>
            <path d="M107.961 23.0266H106.816L103.35 28.5572L99.8672 23.0266H98.6575L98.6094 23.1228L102.769 29.5412V33.5069H103.931V29.5079L108.009 23.1228L107.961 23.0266Z" fill="#363636"/>
            <path d="M109.905 23.3725H110.619V25.4793H111.071V23.3725H111.801V23.0266H109.905V23.3725Z" fill="#363636"/>
            <path d="M114.08 23.0266L113.276 24.8615L112.479 23.0266H112.155L111.885 25.4793H112.312L112.471 24.0051L113.109 25.4793H113.433L114.08 24.0051L114.247 25.4793H114.674L114.397 23.0266H114.08Z" fill="#363636"/>
          </g>
          <defs>
            <clipPath id="clip0_15275_2258">
              <rect width="150" height="33.507" fill="white"/>
            </clipPath>
          </defs>
        </svg>
      </div>

      <!-- Email/Phone Input -->
      <div class="sc-give-form-trial__signin-form-group">
        <input 
          type="text" 
          placeholder="Email or phone" 
          class="sc-give-form-trial__signin-input"
          disabled
        />
      </div>

      <!-- Continue Button -->
      <button class="sc-give-form-trial__signin-button" disabled>
        Continue
      </button>

      <!-- Divider -->
      <div class="sc-give-form-trial__signin-divider">
        <span>or</span>
      </div>

      <!-- Create Account Button -->
      <button class="sc-give-form-trial__signin-secondary-button" disabled>
        Create Account
      </button>

      <!-- Forgot Password Link -->
      <div class="sc-give-form-trial__signin-footer">
        <a href="#" class="sc-give-form-trial__signin-link" disabled>Forgot Password</a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    settings: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeTab: 'give'
    }
  },
  methods: {
    handleLearnMoreClick() {
      const learnMoreUrl = this.settings && this.settings.amplify_learn_more_url;
      if (learnMoreUrl) {
        window.open(learnMoreUrl, '_blank');
      }
      this.$emit('learn-more-clicked');
    },
    handleUpgradeClick() {
      const upgradeUrl = this.settings && this.settings.amplify_upgrade_url;
      if (upgradeUrl) {
        window.open(upgradeUrl, '_blank');
      }
      this.$emit('upgrade-clicked');
    }
  }
}
</script>

<style lang="scss" scoped>
.sc-give-form-trial {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  overflow-y: auto;
}

// Alert Banner
.sc-give-form-trial__alert {
  padding: 12px;
  margin: 16px 26.5px 0;
  background: #FDF7F3;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
}

.sc-give-form-trial__alert-text {
  color: #9F522E;
  font-size: 16px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 400;
  line-height: 24px;
  word-wrap: break-word;
}

.sc-give-form-trial__alert-icon {
  color: #9F522E;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
}

// ── Tooltip wrapper — both icon and panel live here so :hover never breaks ──
.sc-give-form-trial__tooltip-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;

  // Show tooltip on hover of the whole wrapper
  &:hover .sc-give-form-trial__tooltip {
    display: block;
  }
}

// Tooltip panel
.sc-give-form-trial__tooltip {
  display: none;
  position: absolute;
  top: calc(100% + 10px); // 10px gap below icon
  right: -8px;            // aligned toward icon
  z-index: 9999;
  width: 264px;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
}

// White CSS-triangle arrow (no Bootstrap Vue override needed at all)
.sc-give-form-trial__tooltip-arrow {
  position: absolute;
  top: -11px;
  right: 5px;
  width: 0;
  height: 0;
  border-left: 11px solid transparent;
  border-right: 11px solid transparent;
  border-bottom: 11px solid #ffffff;
  // drop-shadow gives the subtle top-edge border look from Figma
  filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.08));
}

// Inner content
.sc-give-form-trial__tooltip-inner {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sc-give-form-trial__tooltip-title {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 15px;
  color: #484848;
  margin: 0;
}

.sc-give-form-trial__tooltip-body {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  color: #333333;
  margin: 0;
  word-break: break-word;
}

.sc-give-form-trial__tooltip-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  margin-top: 4px;
}

.sc-give-form-trial__tooltip-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  font-family: 'Red Hat Text', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: #1863CC;
  text-decoration: none;
  white-space: nowrap;
  border-radius: 4px;

  &:hover {
    text-decoration: underline;
    color: #1863CC;
  }
}

.sc-give-form-trial__tooltip-upgrade {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 12px;
  background: #1863CC;
  color: #ffffff;
  border-radius: 4px;
  font-family: 'Red Hat Text', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: #1453a8;
    color: #ffffff;
    text-decoration: none;
  }
}

// Tabs
.sc-give-form-trial__tabs {
  display: flex;
  gap: 0;
  margin-top: 8px;
  padding: 0 10.5px;
  border-bottom: 2px solid #E1E1E1;
}

.sc-give-form-trial__tab {
  flex: 1;
  padding: 6px 0;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  outline: none;
  cursor: pointer;
  color: #363636;
  font-size: 14px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 400;
  line-height: 22px;
  opacity: 0.6;
  transition: all 0.3s ease;
  position: relative;

  &--active {
    color: #353C43;
    font-weight: 600;
    opacity: 1;

    &::after {
      content: '';
      position: absolute;
      bottom: -3px;
      left: 50%;
      transform: translateX(-50%);
      width: 50%;
      height: 2px;
      background: #1863CC;
    }
  }

  &:hover:not(.sc-give-form-trial__tab--active) {
    opacity: 0.8;
  }
}

// Divider
.sc-give-form-trial__divider {
  height: 2px;
  background: #E1E1E1;
  margin: 16px 10.5px 0;
}

// Content
.sc-give-form-trial__content {
  flex: 1;
  padding: 24px 10.5px 36px 10.5px;
  overflow-y: auto;
  position: relative;

  &--signin {
    padding: 28px 10.5px 24px 10.5px;
  }
}

// Title
.sc-give-form-trial__title {
  text-align: center;
  color: #363636;
  font-size: 16px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 600;
  line-height: 24px;
  margin-bottom: 16px;
}

// Amount Display
.sc-give-form-trial__amount-display {
  text-align: center;
  color: #B5B5B5;
  font-size: 54px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 400;
  line-height: 74px;
  margin-bottom: 0;
}

// Amount Divider
.sc-give-form-trial__amount-divider {
  height: 2px;
  background: #1863CC;
  margin-bottom: 46px;
  width: 35%;
  margin-left: auto;
  margin-right: auto;
}

// Gift Selector
.sc-give-form-trial__gift-selector {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  background: #EBEBEE;
  padding: 3px;
  border-radius: 4px;
}

.sc-give-form-trial__gift-option {
  flex: 1;
  padding: 4px 10px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #6B6B6B;
  font-size: 12px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 600;
  line-height: 20px;
  cursor: not-allowed;
  opacity: 0.6;
  transition: all 0.3s ease;

  &--active {
    background: #ffffff;
    color: #363636;
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
  }
}

// Fund Selector
.sc-give-form-trial__fund-selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 4px 12px;
  border: 1px solid #B5B5B5;
  border-radius: 4px;
  background: #ffffff;
  cursor: not-allowed;
  margin-bottom: 16px;
  opacity: 0.6;
}

.sc-give-form-trial__fund-text {
  color: #363636;
  font-size: 14px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 400;
  line-height: 22px;
}

.sc-give-form-trial__fund-icon {
  color: #757575;
  flex-shrink: 0;
}

// Checkbox
.sc-give-form-trial__checkbox-group {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  opacity: 0.6;
}

.sc-give-form-trial__checkbox {
  width: 18px;
  height: 18px;
  cursor: not-allowed;
  border: 2px solid #A6A6A6;
  border-radius: 2px;

  &:disabled {
    cursor: not-allowed;
  }
}

.sc-give-form-trial__checkbox-label {
  color: #363636;
  font-size: 14px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 400;
  line-height: 22px;
  cursor: not-allowed;
  user-select: none;
}

// Give Button
.sc-give-form-trial__give-button {
  width: 100%;
  padding: 10px;
  background: #1863CC;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 20px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 600;
  line-height: 28px;
  cursor: not-allowed;
  opacity: 0.39;
  margin-bottom: 16px;
  transition: all 0.3s ease;

  &:disabled {
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.8;
  }
}

// Secondary Button (Add Another Gift)
.sc-give-form-trial__secondary-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 5px 12px;
  background: #ffffff;
  color: #C4C4C4;
  border: 1px solid #D2D2D2;
  border-radius: 4px;
  font-size: 14px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 600;
  line-height: 22px;
  cursor: not-allowed;
  opacity: 0.6;
  transition: all 0.3s ease;
  margin-bottom: 32px;

  &:disabled {
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.8;
  }
}

.sc-give-form-trial__secondary-button-icon {
  color: #C4C4C4;
  flex-shrink: 0;
}

// Responsive
@media (max-width: 576px) {
  .sc-give-form-trial__content {
    padding: 12px 8px;
  }

  .sc-give-form-trial__title {
    margin-bottom: 24px;
  }

  .sc-give-form-trial__amount-display {
    font-size: 42px;
    line-height: 66px;
    margin-bottom: 12px;
  }
}

// Scrollbar styling
.sc-give-form-trial::-webkit-scrollbar {
  width: 6px;
}

.sc-give-form-trial::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;

  &:hover {
    background: #999;
  }
}

// Sign In Styles
.sc-give-form-trial__signin-header {
  text-align: center;
  margin-bottom: 21px;
  padding: 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sc-give-form-trial__signin-logo {
  max-width: 100%;
  height: auto;
  max-height: 40px;
}

.sc-give-form-trial__signin-brand {
  color: #363636;
  font-size: 14px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 600;
  line-height: 22px;
}

.sc-give-form-trial__signin-subtext {
  color: #B5B5B5;
  font-size: 12px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 400;
  line-height: 20px;
  margin-top: 4px;
}

.sc-give-form-trial__signin-form-group {
  margin-bottom: 24px;
  max-width: 240px;
  margin-left: auto;
  margin-right: auto;
}

.sc-give-form-trial__signin-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #B5B5B5;
  border-radius: 4px;
  font-size: 16px;
  font-family: 'Red Hat Text', sans-serif;
  line-height: 24px;
  color: #6B6B6B;
  transition: border-color 0.3s ease;

  &::placeholder {
    color: #B5B5B5;
  }

  &:focus {
    outline: none;
    border-color: #1863CC;
    box-shadow: 0 0 0 3px rgba(24, 99, 204, 0.1);
  }

  &:disabled {
    background-color: #F5F5F5;
    color: #B5B5B5;
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.sc-give-form-trial__signin-button {
  width: 100%;
  max-width: 240px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  padding: 8px 12px;
  background: #1863CC;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  transition: background 0.3s ease;
  margin-bottom: 16px;

  &:hover {
    background: #1453a8;
  }

  &:active {
    background: #0f3d7f;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.sc-give-form-trial__signin-divider {
  display: flex;
  align-items: center;
  margin: 22px auto;
  max-width: 240px;
  color: #B5B5B5;
  font-size: 14px;
  font-family: 'Red Hat Text', sans-serif;
  line-height: 22px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #E1E1E1;
  }

  &::before {
    margin-right: 8px;
  }

  &::after {
    margin-left: 8px;
  }

  span {
    display: inline;
  }
}

.sc-give-form-trial__signin-secondary-button {
  width: 100%;
  max-width: 240px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  padding: 8px 12px;
  background: unset;
  color: #6B6B6B;
  border: 1px solid #A6A6A6;
  border-radius: 4px;
  font-size: 16px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  transition: background 0.3s ease;
  margin-bottom: 40px;

  &:hover {
    background: #DCDCE0;
  }

  &:active {
    background: #C8C8CD;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.sc-give-form-trial__signin-footer {
  text-align: center;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.sc-give-form-trial__signin-link {
  color: #1863CC;
  font-size: 14px;
  font-family: 'Red Hat Text', sans-serif;
  font-weight: 600;
  line-height: 22px;
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;

  &:hover {
    color: #1453a8;
    text-decoration: underline;
  }

  &:disabled,
  &[disabled] {
    cursor: not-allowed;
    opacity: 0.6;

    &:hover {
      text-decoration: none;
      color: #B5B5B5;
    }
  }
}
</style>

<!-- No global Bootstrap Vue popover overrides needed — tooltip is now CSS-only -->
<style lang="scss">
</style>